import { useAppStore } from '@core/store'
import { clashKey, type ClashResult } from '@viewer/clash/lib'
import { agrupar, type ConflitoParaAgrupar, type GrupoCalculado } from './clashAgrupamento'
import { chavesFixas, gruposAnteriores, type ClashClassificacaoFile, type ItemClassificado } from './clashAgrupamentoEstado'
import {
  classificarConflito,
  DESCRICAO_DA_REGRA,
  type ClassificacaoDoConflito,
  type NivelDeReferencia,
} from './clashClassificacao'
import { descreverConflitos, niveisDeReferencia, type ProgressoDaDescricao } from './clashClassificacaoDados'
import { mapaDeEspecialidades } from './clashEspecialidades'
import { clashLabel, temDecisao, type ClashTriageMap } from './clashTriage'

// ---------------------------------------------------------------------------
// "Classificar automaticamente" e "Agrupar": a ponte entre o painel, o
// adaptador do viewer (clashClassificacaoDados.ts) e as partes puras
// (clashClassificacao.ts, clashAgrupamento.ts, clashAgrupamentoEstado.ts).
//
// A leitura dos modelos (propriedades, geometria, volumes comuns) e o que
// custa; fica em cache para os mesmos resultados e as mesmas
// especialidades, e mudar so os limiares reclassifica sem voltar a ler.
// ---------------------------------------------------------------------------

export interface LeituraDosConflitos {
  porChave: Map<string, ClassificacaoDoConflito>
  guids: Record<string, [string | null, string | null]>
  niveis: NivelDeReferencia[]
  /** Pontos (Z para cima) por chave, para o agrupamento. */
  pontos: Map<string, [number, number, number]>
}

let cache: {
  results: readonly ClashResult[]
  chaveDosDados: string
  dados: Awaited<ReturnType<typeof descreverConflitos>>
  niveis: NivelDeReferencia[]
} | null = null

/** Le e classifica os conflitos com as regras actuais. */
export async function lerEClassificar(
  results: readonly ClashResult[],
  estado: ClashClassificacaoFile,
  opts: { onProgress?: (p: ProgressoDaDescricao) => void; signal?: { aborted: boolean } } = {},
): Promise<LeituraDosConflitos> {
  const modelos = useAppStore.getState().models
  const especialidades = mapaDeEspecialidades(modelos, estado.especialidades)
  const chaveDosDados = JSON.stringify([especialidades, estado.regras.profundidadeMm])
  if (!cache || cache.results !== results || cache.chaveDosDados !== chaveDosDados) {
    const dados = await descreverConflitos(results, especialidades, {
      profundidadeM: estado.regras.profundidadeMm / 1000,
      onProgress: opts.onProgress,
      signal: opts.signal,
    })
    // Cancelado a meio: nada fica em cache nem se aplica.
    if (opts.signal?.aborted) throw new Error('Leitura cancelada.')
    const niveis = await niveisDeReferencia(especialidades)
    cache = { results, chaveDosDados, dados, niveis }
  }
  const porChave = new Map<string, ClassificacaoDoConflito>()
  const pontos = new Map<string, [number, number, number]>()
  for (const [k, c] of cache.dados.itens) {
    porChave.set(k, classificarConflito(c, estado.regras, cache.niveis))
    pontos.set(k, c.point)
  }
  return { porChave, guids: cache.dados.guids, niveis: cache.niveis, pontos }
}

/** Esquece a leitura (os modelos mudaram). */
export function esquecerLeitura(): void {
  cache = null
}

/** Os itens para escrever na triagem. */
export function itensClassificados(results: readonly ClashResult[], leitura: LeituraDosConflitos): ItemClassificado[] {
  const out: ItemClassificado[] = []
  for (const r of results) {
    const k = clashKey(r)
    const c = leitura.porChave.get(k)
    if (!c) continue
    const g = leitura.guids[k]
    out.push({
      key: k,
      classificacao: c,
      guids: g && g[0] && g[1] ? [g[0], g[1]] : null,
      label: clashLabel(r),
    })
  }
  return out
}

/** Os conflitos para o agrupamento, com a prioridade efectiva (a da
 *  triagem, quando houver; senao a da regra). */
export function conflitosParaAgrupar(
  results: readonly ClashResult[],
  leitura: LeituraDosConflitos,
  triagem: ClashTriageMap,
): ConflitoParaAgrupar[] {
  const out: ConflitoParaAgrupar[] = []
  for (const r of results) {
    const k = clashKey(r)
    const c = leitura.porChave.get(k)
    const ponto = leitura.pontos.get(k)
    if (!c || !ponto) continue
    const e = triagem[k]
    const prioridade = e && temDecisao(e) && e.priority ? e.priority : c.prioridade
    out.push({
      key: k,
      elementoA: `${r.pair.modelA}:${r.pair.localIdA}`,
      elementoB: `${r.pair.modelB}:${r.pair.localIdB}`,
      guids: leitura.guids[k] ?? [null, null],
      kind: r.kind,
      depth: r.depth ?? null,
      point: ponto,
      classificacao: c,
      prioridade,
    })
  }
  return out
}

/** Agrupa com o metodo gravado, respeitando as edicoes a mao. */
export function calcularGrupos(
  results: readonly ClashResult[],
  leitura: LeituraDosConflitos,
  triagem: ClashTriageMap,
  estado: ClashClassificacaoFile,
): GrupoCalculado[] {
  return agrupar(conflitosParaAgrupar(results, leitura, triagem), estado.agrupamento, {
    fixos: chavesFixas(triagem),
    anteriores: gruposAnteriores(triagem, estado.grupos),
    descricaoDaRegra: (r) => DESCRICAO_DA_REGRA[r]?.(estado.regras) ?? r,
    ordemNiveis: [...leitura.niveis].sort((a, b) => a.cota - b.cota).map((n) => n.nome),
  })
}
